import { useMediaQuery } from '@mui/material';
import { GridEventListener, useGridApiRef } from '@mui/x-data-grid';
import { type MRT_ColumnDef } from 'mantine-react-table';
import moment from 'moment';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Earnings } from '../../../../../app/Slices/SevenDayApiTypes';
import {
    formatCurrencyShort,
    getPaymentStatus,
} from '../../../../../shared/utils';
import GenericTable, {
    LinkType,
    tableBodyCellsStyles,
    tableHeadCellStyles,
} from '../../../../shared/GenericTable';

export default function HostPaymentsReportList({
    earnings,
    serviceFeePercentage,
}: {
    earnings: Earnings;
    serviceFeePercentage: number;
}) {
    const {
        bills: payments,
        bookingsTotal,
        amount,
        serviceFeeAmount,
        refundsTotal,
    } = earnings;

    const navigate = useNavigate();

    const handleRowClick: GridEventListener<'rowClick'> = ({
        row: { id, ownerId, year, month },
    }) => {
        if (id === 'total') return;
        navigate(`/host-payment/${ownerId}/${year}/${month}`, {
            state: { name: 'hostPayment' },
        });
    };

    const columns: MRT_ColumnDef<any>[] = [
        {
            accessorKey: 'period',
            header: 'Period',
        },
        {
            accessorKey: 'ownerName',
            header: 'Owner',
        },
        {
            accessorKey: 'amount',
            header: 'Amount',
            Cell: ({ cell }) => formatCurrencyShort(cell.getValue<number>()),
            filterFn: 'betweenInclusive',
            mantineTableHeadCellProps: {
                align: 'right',
            },
            mantineTableBodyCellProps: {
                align: 'right',
            },
        },
        {
            accessorKey: 'serviceFeeAmount',
            header: `7DAY service Fee (${serviceFeePercentage}%)`,
            size: 250,
            Cell: ({ cell }) => formatCurrencyShort(cell.getValue<number>()),
            columnDefType: 'data',
            filterFn: 'betweenInclusive',
            mantineTableHeadCellProps: {
                align: 'right',
            },
            mantineTableBodyCellProps: {
                align: 'right',
            },
        },

        {
            accessorKey: 'refundTotal',
            header: 'Refund',
            Cell: ({ cell }) => formatCurrencyShort(cell.getValue<number>()),
            filterFn: 'betweenInclusive',
            mantineTableHeadCellProps: {
                align: 'right',
            },
            mantineTableBodyCellProps: {
                align: 'right',
            },
        },
        {
            accessorKey: 'gross',
            header: 'Gross',
            Cell: ({ cell }) => formatCurrencyShort(cell.getValue<number>()),
            filterFn: 'betweenInclusive',
            mantineTableHeadCellProps: {
                align: 'right',
            },
            mantineTableBodyCellProps: {
                align: 'right',
            },
        },
        {
            accessorKey: 'paidAt',
            header: 'Paid Date',

            mantineTableHeadCellProps: {
                style: { ...tableHeadCellStyles, paddingLeft: 40 },
            },
            mantineTableBodyCellProps: {
                style: { ...tableBodyCellsStyles, paddingLeft: 40 },
            },
        },
        {
            accessorKey: 'status',
            header: 'Status',
            filterVariant: 'select',
            filterFn: (row, columnId, filterValue) => {
                if (!filterValue) return true;
                const value = row.getValue(columnId);
                return value === filterValue;
            },
            mantineFilterSelectProps: {
                data: [
                    { label: 'Paid', value: 'Paid' },
                    { label: 'Unpaid', value: 'Unpaid' },
                ],
                clearable: true,
            },
        },
    ];

    const rows = [
        ...payments
            .filter((payment) => payment.amount)
            .map((payment) => {
                const {
                    periodStart,
                    periodEnd,
                    ownerName,
                    bookingsTotal: amount,
                    serviceFeeAmount,
                    amount: gross,
                    refundTotal,
                    paidAt,
                    ownerId,
                    year,
                    month,
                } = payment;
                return {
                    id: `${ownerId}-${year}-${month}`,
                    period: `${moment(new Date(periodStart)).format(
                        'D MMM',
                    )} - ${moment(new Date(periodEnd)).format('D MMM, YYYY')}`,
                    periodStart,
                    periodEnd,
                    ownerName,
                    amount,
                    serviceFeeAmount,
                    gross,
                    refundTotal,
                    paidAt: paidAt ? moment(paidAt).format('D MMM YYYY') : '',
                    status: getPaymentStatus(payment),
                    ownerId,
                    year,
                    month,
                };
            }),
        {
            id: 'total',
            ownerName: 'Total',
            amount: bookingsTotal,
            serviceFeeAmount,
            gross: amount,
            refundTotal: refundsTotal,
        },
    ];

    const linkState = { name: 'hostPayment' };

    return (
        <GenericTable
            columns={columns}
            data={rows}
            tableName='Host-Payments-Report'
            loading={!earnings}
            linkType={LinkType.Bill}
            linkState={linkState}
        />
    );
}
